import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { ButtonRowBlock, ButtonRowSlot } from '../../domain/types';
import type { RenderTheme } from '../../domain/text-theme';
import {
  buttonChromeCellStyle,
  buttonChromeLinkStyle,
} from '../../domain/blocks/button-presentation';
import { resolveButtonChrome } from '../../domain/button-theme';
import {
  buttonRowCellWidth,
  buttonRowExpandsHorizontally,
  buttonRowSlotFillsHorizontally,
} from '../../domain/button-row-layout';
import { blockAlignShell } from '../block-align-shell';
import { padStyle, presentationTableProps } from '../table';
import { linkPropsForMode } from './editor-props';
import { merge } from './merge';
import type { BlockRenderProps } from './types';
import {
  emailBuilderDisplayedLabelFontWeight,
  isEmailBuilderRichTextEmpty,
} from '../../editing/richText/emailBuilderRichTextHtml';
import { renderEmailBuilderButtonLabelContent } from './button';

function ButtonCell({
  slot,
  fill,
  mergeMode,
  editorMode,
  textTheme,
  labelSlot,
}: {
  slot: ButtonRowSlot;
  fill: boolean;
  mergeMode: BlockRenderProps['mergeMode'];
  editorMode: boolean;
  textTheme: RenderTheme;
  labelSlot?: ReactNode;
}) {
  const chrome = resolveButtonChrome(slot, textTheme);
  const cellStyle: CSSProperties = {
    ...buttonChromeCellStyle(chrome),
    ...(fill ? { width: '100%' } : {}),
  };
  const linkStyle: CSSProperties = {
    ...buttonChromeLinkStyle({
      ...slot,
      ...chrome,
      fontWeight: emailBuilderDisplayedLabelFontWeight(slot.label, slot.fontWeight),
    }, textTheme.bodyFontFamily),
    ...(fill
      ? { display: 'block', width: '100%', boxSizing: 'border-box' }
      : {}),
  };

  const linkProps = linkPropsForMode(
    editorMode,
    merge(slot.href, mergeMode) || '',
  );
  const label = merge(slot.label, mergeMode);
  if (!editorMode && isEmailBuilderRichTextEmpty(label)) {
    return null;
  }
  const labelContent = renderEmailBuilderButtonLabelContent(label);

  return (
    <table
      {...presentationTableProps()}
      className={fill ? 'btn-row-inner btn-row-fill' : 'btn-row-inner'}
      style={{
        display: fill ? 'table' : 'inline-table',
        width: fill ? '100%' : undefined,
        borderCollapse: 'collapse',
      }}>
      <tbody>
        <tr>
          <td style={cellStyle}>
            {labelSlot ??
              (editorMode ? (
                <span className="btn-row-link" style={linkStyle}>
                  {labelContent}
                </span>
              ) : linkProps ? (
                <a className="btn-row-link" {...linkProps} style={linkStyle}>
                  {labelContent}
                </a>
              ) : (
                <span className="btn-row-link" style={linkStyle}>
                  {labelContent}
                </span>
              ))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function renderButtonRowBlock({
  block,
  textTheme,
  mergeMode,
  editorMode = false,
  wrapSlot,
  slotLabelSlots,
}: BlockRenderProps & {
  block: ButtonRowBlock;
  wrapSlot?: (index: number, node: ReactNode) => ReactNode;
  slotLabelSlots?: readonly [ReactNode?, ReactNode?];
}) {
  const expands = buttonRowExpandsHorizontally(block);
  const stackClasses = block.stackOnMobile
    ? ['btn-row-stack', block.stackFullWidth ? 'btn-row-stack-full' : '']
        .filter(Boolean)
        .join(' ')
    : undefined;

  const rowTable = (
    <table
      {...presentationTableProps()}
      className={stackClasses}
      style={{
        display: expands ? 'table' : 'inline-table',
        width: expands ? '100%' : undefined,
        borderCollapse: 'collapse',
        ...(block.stackOnMobile
          ? { ['--btn-row-gap' as string]: `${block.gap}px` }
          : {}),
      }}>
      <tbody>
        <tr>
          {block.buttons.map((slot, index) => {
            const fill = buttonRowSlotFillsHorizontally(block, index);
            const cellWidth = buttonRowCellWidth(block, index);
            const cellClasses = ['btn-row-cell', fill ? 'btn-row-fill' : '']
              .filter(Boolean)
              .join(' ');
            const buttonCell = (
              <ButtonCell
                slot={slot}
                fill={fill}
                mergeMode={mergeMode}
                editorMode={editorMode}
                textTheme={textTheme}
                labelSlot={slotLabelSlots?.[index]}
              />
            );

            return (
              <td
                key={index}
                width={cellWidth}
                className={cellClasses}
                style={{
                  paddingLeft: index === 0 ? 0 : block.gap,
                  verticalAlign: 'top',
                  ...(cellWidth ? { width: cellWidth } : {}),
                }}>
                {wrapSlot ? wrapSlot(index, buttonCell) : buttonCell}
              </td>
            );
          })}
        </tr>
      </tbody>
    </table>
  );

  return blockAlignShell(block.align, rowTable, {
    ...padStyle(block.padding),
    lineHeight: 0,
  });
}
